/**
 * history-overlay.js
 * @author: Bernhard Wolkerstorfer
 * 
 * Description: Overlay for history.
 * 
 * Copyright (c) 2012 Amazon.com, Inc. All rights reserved.
 */
import { getState } from "../state/state";
import {addFocusLimitation, removeFocusLimitation} from "../../src-common/utils/s2k-focus-helper";

/**
 * @constructor
 */
const History = function () {
};

History.prototype = {
    // ######## Constants ########
    /**
     * URL for the history.
     * @const
     * @type {string}
     */
    HISTORY_URL: "/send-to-kindle-history.html",
        
    /**
     * History styling information.
     * @const
     * @type {Object}
     */
    HISTORY_STYLE: {
        "#s2k-history-overlay": {
            "position": "fixed",
            "width": "100%",
            "top": "0",
            "left": "0",
            "z-index": "2147483647"
        },
        "#s2k-history-frame": {
            "width": "100%",
            "border-bottom-left-radius": "15px",
            "border-bottom-right-radius": "15px",
            "filter": "drop-shadow(0px 4px 14px rgba(15, 17, 17, 0.25))"
        }
    },
 
    /**
     * History Overlay
     * @const
     * @type {Object}
     */    
    HISTORY_OVERLAY: 
        $('<div id="s2k-history-overlay" style="display:none">' +
          '   <iframe sandbox="allow-scripts allow-popups" id="s2k-history-frame" frameborder="0" scrolling="0" ></iframe>' +
          '</div>'
        ),
        
    // ######## Members ########
    /**
     * History Overlay
     * @private
     * @type {Object|undefined}
     */
    history: undefined,

    /**
     * History Frame
     * @private
     * @type {Node|undefined}
     */
    historyFrame: undefined,

    /**
     * Whether has history
     * @private
     * @type {boolean}
     */
    hasHistory: true,
        
    // ######## Methods ########
    /**
     * Inject the history overlay into the platform.
     */
    inject: function () {
        // Load the state for this window/tab.
        const state = getState();

        // Create the overlay injection HTML.
        this.history = this.HISTORY_OVERLAY.clone();
        let extensionId = chrome.i18n.getMessage("@@extension_id");
        state.extensionUrl = `chrome-extension://${extensionId}`;
        let url = state.extensionUrl + this.HISTORY_URL;
        this.history.find("#s2k-history-frame").attr("src", url);
        
        // Inject the overlay.
        $(state.stateDocument.body).append(this.history);
        
        // Load injected node.
        this.history = $(state.stateDocument.body).find("#s2k-history-overlay");
        this.historyFrame = this.history.find("#s2k-history-frame");
    
        // Attach message listener to window.
        $(state.stateWindow).on("resize", this.onResize.bind(this));
        $(state.stateWindow).on("message", this.onHistoryMessage.bind(this));
        
        // Attach event listener for document clicks.
        $(state.stateDocument).on("click", this.onClose.bind(this));
        
        // Attach event listener for overlay elements.
        this.history.on("click", function (event) {
            event.stopPropagation();
        });
 
        // Apply the stylesheet.
        this.history.css(this.HISTORY_STYLE["#s2k-history-overlay"]);
        for (let style in this.HISTORY_STYLE) {
            this.history.find(style).css(this.HISTORY_STYLE[style]);
        }

        // Init the size of history window
        this.onResize();
        
        // Show the history.
        this.history.slideDown("fast");
    },
      
    /**
     * Terminate the history overlay.
     */
    terminate: function () {
        this.history.slideUp("fast", function () {
            // Remove history from DOM.
            this.history.remove();
        }.bind(this));
    },


    // ######## Event Handler ########
    /**
     * Event handler for window resizes.
     */
    onResize: function () {
        // Load the state for this window/tab.
        let stateWindow = $(getState().stateWindow);
        let width = stateWindow.width();
        // Expected height for normal view is 460px for no-data view is 200px
        let expectedHeight = this.hasHistory ? 460 : 230;
        let height = Math.min(stateWindow.height() * 0.6, expectedHeight);

        // Adjust history size.
        this.historyFrame.css({"width": width + "px", "height": height + "px"});
    },

    /**
     * Event handler for window message.
     * @param event Event - currently has `history-close`, `history-no-data`, `history-has-data`.
     */
    onHistoryMessage: function (event) {
        switch (event.originalEvent.data) {
            case "history-close":
                if (event.originalEvent.data === "history-close") {
                    this.onClose();
                }
                break;
            case "history-no-data":
                this.hasHistory = false;
                this.onResize();
                break;
            case "history-has-data":
                this.hasHistory = true;
                this.onResize();
                break;
        }
    },
    
    /**
     * Event handler for closing the history.
     */
    onClose: function () {
        removeFocusLimitation("s2k-history-container");
        this.terminate();
    }
};

export { History };
